'use client';

import {
  type Dispatch,
  type HTMLAttributes,
  type HTMLProps,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SetStateAction,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import classnames from 'classnames';

import type { BaseComponentProps } from '../base-component.types';
import type { DialogProps, DialogTriggerProps } from './Dialog.types';

import styles from './Dialog.module.scss';

export function useDialog({
  initialOpen = false,
  strategy,
  openAnimation = 'fadeIn',
  closeAnimation = 'fadeOut',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: DialogProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    strategy,
    open,
    onOpenChange: setOpen,
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
  });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return useMemo(
    () => ({
      openAnimation,
      closeAnimation,
      open,
      setOpen,
      ...interactions,
      ...data,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [
      openAnimation,
      closeAnimation,
      open,
      setOpen,
      interactions,
      data,
      labelId,
      descriptionId,
    ],
  );
}

type ContextType =
  | (ReturnType<typeof useDialog> & {
      setLabelId: Dispatch<SetStateAction<string | undefined>>;
      setDescriptionId: Dispatch<SetStateAction<string | undefined>>;
    })
  | null;

const DialogContext = createContext<ContextType>(null);

export const useDialogContext = () => {
  const context = useContext(DialogContext);

  if (context == null) {
    throw new Error('Dialog components must be wrapped in <Dialog />');
  }

  return context;
};

export function Dialog({
  children,
  ...options
}: {
  children: ReactNode;
} & DialogProps) {
  const dialog = useDialog(options);
  return (
    <DialogContext.Provider value={dialog}>{children}</DialogContext.Provider>
  );
}

export function DialogTrigger({
  children,
  asChild = false,
  testId = 'ui-dialog-trigget',
  ref: propRef,
  ...props
}: HTMLProps<HTMLElement> & DialogTriggerProps & { ref?: Ref<HTMLElement> }) {
  const context = useDialogContext();
  const childrenRef =
    asChild && isValidElement(children)
      ? (children.props as { ref?: Ref<HTMLElement> }).ref
      : null;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    const child = children as ReactElement;
    const childProps = child.props as HTMLAttributes<HTMLElement>;

    return cloneElement(child, {
      // eslint-disable-next-line react-hooks/refs
      ...context.getReferenceProps({
        ...(props as HTMLAttributes<HTMLElement>),
        ...childProps,
        ref,
        'data-state': context.open ? 'open' : 'closed',
        'data-testid': testId,
      } as HTMLAttributes<HTMLElement>),
    });
  }

  return (
    <button
      ref={ref}
      data-state={context.open ? 'open' : 'closed'}
      data-testid={testId}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
}

export function DialogContent({
  style,
  className,
  ai,
  testId = 'ui-dialog-content',
  ref: propRef,
  ...props
}: HTMLProps<HTMLDivElement> &
  BaseComponentProps & { ai?: boolean; ref?: Ref<HTMLDivElement> }) {
  const { context: floatingContext, ...context } = useDialogContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    floatingContext,
    {
      duration: {
        open: context.openAnimation === undefined ? undefined : 300,
        close: context.closeAnimation === undefined ? undefined : 300,
      },
      open:
        context.openAnimation === undefined
          ? undefined
          : {
              animationName: context.openAnimation,
              animationDuration: '0.3s',
              animationTimingFunction: 'ease',
              animationFillMode: 'forwards',
            },
      close:
        context.closeAnimation === undefined
          ? undefined
          : {
              animationName: context.closeAnimation,
              animationDuration: '0.3s',
              animationTimingFunction: 'ease',
              animationFillMode: 'forwards',
            },
    },
  );

  const { styles: overlayTransitionStyles } = useTransitionStyles(
    floatingContext,
    {
      duration: {
        open: 200,
        close: 200,
      },
      open: {
        animationName: 'fadeIn',
        animationDuration: '0.2s',
        animationTimingFunction: 'ease',
        animationFillMode: 'forwards',
      },
      close: {
        animationName: 'fadeOut',
        animationDuration: '0.2s',
        animationTimingFunction: 'ease',
        animationFillMode: 'forwards',
      },
    },
  );

  if (!isMounted) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay
        className={styles['overlay']}
        lockScroll
        style={{
          ...overlayTransitionStyles,
        }}
      >
        <FloatingFocusManager context={floatingContext} disabled>
          <div
            ref={ref}
            style={{
              ...transitionStyles,
              ...style,
            }}
            className={classnames(
              styles['dialog'],
              ai && styles['dialog--ai'],
              className,
            )}
            data-testid={testId}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            {...context.getFloatingProps(props)}
          >
            {props.children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}
